import { useState, useEffect } from "react";
import {
	TrendingUp,
	TrendingDown,
	DollarSign,
	Users,
	Eye,
	ShoppingCart,
	ArrowUpRight,
	Activity,
	Moon,
	Sun,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
	AreaChart,
	Area,
	BarChart,
	Bar,
	LineChart,
	Line,
	PieChart,
	Pie,
	Cell,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Legend,
} from "recharts";
import {
	revenueData,
	trafficData,
	categoryData,
	conversionData,
	topPages,
	recentActivity,
} from "@/data/analytics";

function StatCard({
	title,
	value,
	change,
	changeType,
	icon: Icon,
}: {
	title: string;
	value: string;
	change: string;
	changeType: "positive" | "negative";
	icon: React.ComponentType<{ className?: string }>;
}) {
	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between pb-2">
				<CardTitle className="text-sm font-medium text-muted-foreground">
					{title}
				</CardTitle>
				<Icon className="h-4 w-4 text-muted-foreground" />
			</CardHeader>
			<CardContent>
				<div className="text-2xl font-bold">{value}</div>
				<div className="flex items-center gap-1 mt-1">
					{changeType === "positive" ? (
						<TrendingUp className="h-3 w-3 text-emerald-500" />
					) : (
						<TrendingDown className="h-3 w-3 text-red-500" />
					)}
					<span
						className={`text-xs font-medium ${
							changeType === "positive"
								? "text-emerald-500"
								: "text-red-500"
						}`}
					>
						{change}
					</span>
					<span className="text-xs text-muted-foreground">
						from last month
					</span>
				</div>
			</CardContent>
		</Card>
	);
}

const chartTooltipStyle = {
	contentStyle: {
		backgroundColor: "hsl(var(--card))",
		border: "1px solid hsl(var(--border))",
		borderRadius: "var(--radius)",
		fontSize: "12px",
	},
	labelStyle: { color: "hsl(var(--foreground))" },
	itemStyle: { color: "hsl(var(--foreground))" },
};

function RevenueChart() {
	return (
		<Card className="col-span-full lg:col-span-2">
			<CardHeader>
				<div className="flex items-center justify-between">
					<div>
						<CardTitle>Revenue Overview</CardTitle>
						<p className="text-sm text-muted-foreground mt-1">
							Monthly revenue vs expenses
						</p>
					</div>
					<Badge variant="secondary" className="gap-1">
						<ArrowUpRight className="h-3 w-3" />
						+16.2%
					</Badge>
				</div>
			</CardHeader>
			<CardContent>
				<ResponsiveContainer width="100%" height={300}>
					<AreaChart data={revenueData}>
						<defs>
							<linearGradient
								id="revenueGrad"
								x1="0"
								y1="0"
								x2="0"
								y2="1"
							>
								<stop
									offset="5%"
									stopColor="hsl(var(--chart-1))"
									stopOpacity={0.3}
								/>
								<stop
									offset="95%"
									stopColor="hsl(var(--chart-1))"
									stopOpacity={0}
								/>
							</linearGradient>
							<linearGradient
								id="expenseGrad"
								x1="0"
								y1="0"
								x2="0"
								y2="1"
							>
								<stop
									offset="5%"
									stopColor="hsl(var(--chart-2))"
									stopOpacity={0.3}
								/>
								<stop
									offset="95%"
									stopColor="hsl(var(--chart-2))"
									stopOpacity={0}
								/>
							</linearGradient>
						</defs>
						<CartesianGrid
							strokeDasharray="3 3"
							stroke="hsl(var(--border))"
							vertical={false}
						/>
						<XAxis
							dataKey="month"
							stroke="hsl(var(--muted-foreground))"
							fontSize={12}
							tickLine={false}
							axisLine={false}
						/>
						<YAxis
							stroke="hsl(var(--muted-foreground))"
							fontSize={12}
							tickLine={false}
							axisLine={false}
							tickFormatter={(v) => `$${v / 1000}k`}
						/>
						<Tooltip
							{...chartTooltipStyle}
							formatter={(value: number) =>
								`$${value.toLocaleString()}`
							}
						/>
						<Legend />
						<Area
							type="monotone"
							dataKey="revenue"
							stroke="hsl(var(--chart-1))"
							fill="url(#revenueGrad)"
							strokeWidth={2}
							name="Revenue"
						/>
						<Area
							type="monotone"
							dataKey="expenses"
							stroke="hsl(var(--chart-2))"
							fill="url(#expenseGrad)"
							strokeWidth={2}
							name="Expenses"
						/>
					</AreaChart>
				</ResponsiveContainer>
			</CardContent>
		</Card>
	);
}

function TrafficSourcesChart() {
	return (
		<Card>
			<CardHeader>
				<CardTitle>Traffic Sources</CardTitle>
				<p className="text-sm text-muted-foreground">
					Breakdown by channel
				</p>
			</CardHeader>
			<CardContent>
				<ResponsiveContainer width="100%" height={300}>
					<PieChart>
						<Pie
							data={categoryData}
							cx="50%"
							cy="50%"
							innerRadius={60}
							outerRadius={100}
							paddingAngle={4}
							dataKey="value"
							strokeWidth={0}
						>
							{categoryData.map((entry, index) => (
								<Cell key={index} fill={entry.fill} />
							))}
						</Pie>
						<Tooltip
							{...chartTooltipStyle}
							formatter={(value: number) => `${value}%`}
						/>
						<Legend
							formatter={(value) => (
								<span className="text-xs text-muted-foreground">
									{value}
								</span>
							)}
						/>
					</PieChart>
				</ResponsiveContainer>
			</CardContent>
		</Card>
	);
}

function WeeklyTrafficChart() {
	return (
		<Card className="col-span-full lg:col-span-2">
			<CardHeader>
				<div className="flex items-center justify-between">
					<div>
						<CardTitle>Weekly Traffic</CardTitle>
						<p className="text-sm text-muted-foreground mt-1">
							Visitors and page views this week
						</p>
					</div>
					<Badge variant="secondary" className="gap-1">
						<Activity className="h-3 w-3" />
						Live
					</Badge>
				</div>
			</CardHeader>
			<CardContent>
				<ResponsiveContainer width="100%" height={300}>
					<BarChart data={trafficData} barGap={4}>
						<CartesianGrid
							strokeDasharray="3 3"
							stroke="hsl(var(--border))"
							vertical={false}
						/>
						<XAxis
							dataKey="day"
							stroke="hsl(var(--muted-foreground))"
							fontSize={12}
							tickLine={false}
							axisLine={false}
						/>
						<YAxis
							stroke="hsl(var(--muted-foreground))"
							fontSize={12}
							tickLine={false}
							axisLine={false}
						/>
						<Tooltip {...chartTooltipStyle} />
						<Legend />
						<Bar
							dataKey="visitors"
							fill="hsl(var(--chart-1))"
							radius={[4, 4, 0, 0]}
							name="Visitors"
						/>
						<Bar
							dataKey="pageViews"
							fill="hsl(var(--chart-3))"
							radius={[4, 4, 0, 0]}
							name="Page Views"
						/>
					</BarChart>
				</ResponsiveContainer>
			</CardContent>
		</Card>
	);
}

function ConversionChart() {
	return (
		<Card>
			<CardHeader>
				<CardTitle>Conversion Rate</CardTitle>
				<p className="text-sm text-muted-foreground">
					Monthly trend
				</p>
			</CardHeader>
			<CardContent>
				<ResponsiveContainer width="100%" height={300}>
					<LineChart data={conversionData}>
						<CartesianGrid
							strokeDasharray="3 3"
							stroke="hsl(var(--border))"
							vertical={false}
						/>
						<XAxis
							dataKey="month"
							stroke="hsl(var(--muted-foreground))"
							fontSize={12}
							tickLine={false}
							axisLine={false}
						/>
						<YAxis
							stroke="hsl(var(--muted-foreground))"
							fontSize={12}
							tickLine={false}
							axisLine={false}
							tickFormatter={(v) => `${v}%`}
						/>
						<Tooltip
							{...chartTooltipStyle}
							formatter={(value: number) => `${value}%`}
						/>
						<Line
							type="monotone"
							dataKey="rate"
							stroke="hsl(var(--chart-4))"
							strokeWidth={2.5}
							dot={{ fill: "hsl(var(--chart-4))", r: 4 }}
							activeDot={{ r: 6 }}
							name="Rate"
						/>
					</LineChart>
				</ResponsiveContainer>
			</CardContent>
		</Card>
	);
}

function TopPagesTable() {
	return (
		<Card className="col-span-full lg:col-span-2">
			<CardHeader>
				<CardTitle>Top Pages</CardTitle>
				<p className="text-sm text-muted-foreground">
					Most visited pages this month
				</p>
			</CardHeader>
			<CardContent>
				<div className="overflow-x-auto">
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b border-border">
								<th className="text-left font-medium text-muted-foreground pb-3">
									Page
								</th>
								<th className="text-right font-medium text-muted-foreground pb-3">
									Views
								</th>
								<th className="text-right font-medium text-muted-foreground pb-3">
									Unique
								</th>
								<th className="text-right font-medium text-muted-foreground pb-3">
									Bounce
								</th>
							</tr>
						</thead>
						<tbody>
							{topPages.map((page) => (
								<tr
									key={page.page}
									className="border-b border-border/50 last:border-0"
								>
									<td className="py-3 font-medium font-mono text-xs">
										{page.page}
									</td>
									<td className="py-3 text-right tabular-nums">
										{page.views.toLocaleString()}
									</td>
									<td className="py-3 text-right tabular-nums text-muted-foreground">
										{page.unique.toLocaleString()}
									</td>
									<td className="py-3 text-right">
										<Badge
											variant={
												parseInt(page.bounce) > 40
													? "destructive"
													: "secondary"
											}
											className="text-xs"
										>
											{page.bounce}
										</Badge>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</CardContent>
		</Card>
	);
}

function RecentActivityCard() {
	return (
		<Card>
			<CardHeader>
				<CardTitle>Recent Activity</CardTitle>
				<p className="text-sm text-muted-foreground">Latest events</p>
			</CardHeader>
			<CardContent>
				<div className="space-y-4">
					{recentActivity.map((item, i) => (
						<div key={i}>
							<div className="flex items-center gap-3">
								<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
									{item.initials}
								</div>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium truncate">
										{item.user}
									</p>
									<p className="text-xs text-muted-foreground truncate">
										{item.action}
									</p>
								</div>
								<div className="text-right shrink-0">
									{item.amount && (
										<p className="text-sm font-medium">
											{item.amount}
										</p>
									)}
									<p className="text-xs text-muted-foreground">
										{item.time}
									</p>
								</div>
							</div>
							{i < recentActivity.length - 1 && (
								<Separator className="mt-4" />
							)}
						</div>
					))}
				</div>
			</CardContent>
		</Card>
	);
}

export function Dashboard() {
	const [dark, setDark] = useState(() =>
		document.documentElement.classList.contains("dark"),
	);

	useEffect(() => {
		document.documentElement.classList.toggle("dark", dark);
		localStorage.setItem("theme", dark ? "dark" : "light");
	}, [dark]);

	return (
		<div className="min-h-screen bg-background">
			<div className="border-b border-border">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
						<div>
							<h1 className="text-2xl font-bold tracking-tight">
								Analytics Dashboard
							</h1>
							<p className="text-sm text-muted-foreground mt-1">
								Track your key metrics and performance
							</p>
						</div>
						<div className="flex items-center gap-3">
							<Button
								variant="outline"
								size="icon"
								onClick={() => setDark((d) => !d)}
								aria-label="Toggle dark mode"
							>
								{dark ? (
									<Sun className="h-4 w-4" />
								) : (
									<Moon className="h-4 w-4" />
								)}
							</Button>
							<Badge variant="outline" className="w-fit gap-1.5">
								<span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
								Live data
							</Badge>
						</div>
					</div>
				</div>
			</div>

			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Stat Cards */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
					<StatCard
						title="Total Revenue"
						value="$34,500"
						change="+16.2%"
						changeType="positive"
						icon={DollarSign}
					/>
					<StatCard
						title="Active Users"
						value="2,420"
						change="+8.1%"
						changeType="positive"
						icon={Users}
					/>
					<StatCard
						title="Page Views"
						value="42,300"
						change="-3.2%"
						changeType="negative"
						icon={Eye}
					/>
					<StatCard
						title="Conversions"
						value="1,210"
						change="+12.5%"
						changeType="positive"
						icon={ShoppingCart}
					/>
				</div>

				{/* Charts Tabs */}
				<Tabs defaultValue="overview" className="mb-8">
					<TabsList>
						<TabsTrigger value="overview">Overview</TabsTrigger>
						<TabsTrigger value="traffic">Traffic</TabsTrigger>
					</TabsList>

					<TabsContent value="overview" className="mt-6">
						<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
							<RevenueChart />
							<TrafficSourcesChart />
						</div>
					</TabsContent>

					<TabsContent value="traffic" className="mt-6">
						<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
							<WeeklyTrafficChart />
							<ConversionChart />
						</div>
					</TabsContent>
				</Tabs>

				{/* Bottom Section */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
					<TopPagesTable />
					<RecentActivityCard />
				</div>
			</div>
		</div>
	);
}

export default Dashboard;
